import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  REINDEX_STATUS,
  type ReindexStatusResponse,
  type SwapPhase,
  swapPhaseLabel,
  TRIGGER_REINDEX,
  type TriggerReindexResponse,
} from 'src/api/reindex/reindex';

const MAX_CONSECUTIVE_POLL_FAILURES = 10;
const POLL_INTERVAL = 500; // ms — fast enough to catch the Go reindexer's ~800ms batch updates

export type ReindexPhase = 'idle' | 'starting' | 'running' | 'completed' | 'failed' | 'disconnected';

export interface UseReindexReturn {
  phase: ReindexPhase;
  /** Go swap phase (preflight, creating_index, reindexing, ...). Null when not in a swap run. */
  swapPhase: SwapPhase | null;
  /** Human-readable label for the current swap phase, or null when unavailable. */
  swapPhaseLabel: string | null;
  message: string | null;
  completed: number;
  total: number;
  mutationLoading: boolean;
  handleReindex: () => Promise<void>;
  retry: () => void;
}

export interface UseReindexOptions {
  onComplete?: (total: number) => void;
  onError?: (message: string | null) => void;
}

const useReindex = (options?: UseReindexOptions): UseReindexReturn => {
  const [phase, setPhase] = useState<ReindexPhase>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const hasCheckedInitial = useRef(false);
  const hasSeenRunning = useRef(false);
  const hasTriggered = useRef(false);
  const consecutiveFailures = useRef(0);
  const awaitingFreshPoll = useRef(false);

  // Store callbacks in refs so they can be called at transition time without
  // causing effect re-runs or stale closures.
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const isActive = phase === 'starting' || phase === 'running';

  const [triggerReindex, { loading: mutationLoading }] = useMutation<TriggerReindexResponse>(TRIGGER_REINDEX);

  // Always run the query (no skip) so the initial mount fetch can detect
  // a reindex that is already in progress. Poll only while active.
  const {
    data: statusData,
    error: statusError,
    startPolling,
    stopPolling,
  } = useQuery<ReindexStatusResponse>(REINDEX_STATUS, {
    pollInterval: isActive ? POLL_INTERVAL : 0,
    fetchPolicy: 'network-only',
  });

  // Track consecutive poll failures. If too many, transition to 'disconnected'.
  useEffect(() => {
    if (!isActive) return;
    if (statusError) {
      consecutiveFailures.current += 1;
      if (consecutiveFailures.current > MAX_CONSECUTIVE_POLL_FAILURES) {
        console.warn(
          `Reindex status polling failed ${consecutiveFailures.current} times consecutively. Stopping poll.`,
        );
        stopPolling();
        setPhase('disconnected');
        setMessage('Lost connection to server while monitoring reindex progress. The job may still be running.');
        hasSeenRunning.current = false;
        hasTriggered.current = false;
        awaitingFreshPoll.current = false;
        consecutiveFailures.current = 0;
      }
    } else if (statusData) {
      consecutiveFailures.current = 0;
      if (awaitingFreshPoll.current && isActive) {
        awaitingFreshPoll.current = false;
      }
    }
  }, [statusData, statusError, isActive, stopPolling]);

  // On mount, check if a reindex is already running (e.g. page refresh mid-reindex).
  useEffect(() => {
    if (hasCheckedInitial.current) return;
    const status = statusData?.reindexStatus?.status;
    if (status == null) return; // query hasn't resolved yet
    hasCheckedInitial.current = true;
    if (status === 'running') {
      hasSeenRunning.current = true;
      setPhase('running');
    }
    // If status is 'done' or 'error' on mount and we didn't trigger it, stay idle.
    // This prevents stale terminal states from a prior run from showing alerts.
  }, [statusData]);

  // React to status transitions while active.
  // Require seeing 'running' at least once before treating 'done'/'error' as
  // terminal — unless we just triggered the mutation (hasTriggered), which
  // handles the fast-run case where the job completes before 'running' is polled.
  useEffect(() => {
    if (!isActive) return;
    const status = statusData?.reindexStatus?.status;
    if (status === 'running') {
      hasSeenRunning.current = true;
      if (phase !== 'running') setPhase('running');
    }
    const canAcceptTerminal = (hasSeenRunning.current || hasTriggered.current) && !awaitingFreshPoll.current;
    if (!canAcceptTerminal) return;

    if (status === 'done') {
      const total = statusData?.reindexStatus?.total ?? 0;
      stopPolling();
      setPhase('completed');
      setMessage(`Reindex complete \u2014 ${total} items indexed`);
      hasSeenRunning.current = false;
      hasTriggered.current = false;
      onCompleteRef.current?.(total);
    } else if (status === 'error') {
      const rawError = statusData?.reindexStatus?.error ?? 'Reindex failed';
      const failedPhase = statusData?.reindexStatus?.phase ?? null;
      const phaseLabel = swapPhaseLabel(failedPhase);
      // Surface the phase so operators know where the run died before they retry.
      const errorMsg =
        phaseLabel && !rawError.toLowerCase().includes(phaseLabel.toLowerCase())
          ? `Reindex failed during ${phaseLabel.toLowerCase()}: ${rawError}`
          : rawError;
      stopPolling();
      setPhase('failed');
      setMessage(errorMsg);
      hasSeenRunning.current = false;
      hasTriggered.current = false;
      onErrorRef.current?.(errorMsg);
    }
  }, [statusData, stopPolling, isActive, phase]);

  const handleReindex = useCallback(async (): Promise<void> => {
    // Clear previous state
    hasSeenRunning.current = false;
    consecutiveFailures.current = 0;
    setPhase('starting');
    setMessage(null);

    try {
      const result = await triggerReindex();
      if (result.data?.triggerReindex?.success) {
        hasTriggered.current = true;
        awaitingFreshPoll.current = true;
        setPhase('running');
        startPolling(POLL_INTERVAL);
      } else {
        // Mutation was rejected (e.g. "already in progress", "binary not found")
        const msg = result.data?.triggerReindex?.message ?? 'Reindex request was rejected';
        setPhase('idle');
        setMessage(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start reindex';
      setPhase('idle');
      setMessage(msg);
    }
  }, [triggerReindex, startPolling]);

  const retry = useCallback(() => {
    hasSeenRunning.current = false;
    hasTriggered.current = false;
    hasCheckedInitial.current = false;
    awaitingFreshPoll.current = false;
    consecutiveFailures.current = 0;
    setPhase('idle');
    setMessage(null);
  }, []);

  const currentSwapPhase = statusData?.reindexStatus?.phase ?? null;

  return {
    phase,
    swapPhase: currentSwapPhase,
    swapPhaseLabel: swapPhaseLabel(currentSwapPhase),
    message,
    completed: statusData?.reindexStatus?.completed ?? 0,
    total: statusData?.reindexStatus?.total ?? 0,
    mutationLoading,
    handleReindex,
    retry,
  };
};

export default useReindex;
